import i18n from "../i18n";

// English labels of the built-in methods; the API expects the keys on the left
// as `payment_method` whatever language the guest picked.
const BUILT_IN_METHODS = {
  cash_on_delivery: "Cash on Delivery",
  card_on_delivery: "Card on Delivery",
  room_credit: "Room Credit",
  pay_online: "Pay Online",
};

const normalize = (value) =>
  String(value ?? "")
    .normalize("NFKC")
    .toLowerCase()
    .replace(/[ً-ٰٟـ]/g, "")
    .replace(/[^\p{L}\p{M}\p{N}]+/gu, " ")
    .trim();

let labelIndex;

// Maps every known spelling (key, English label, label in each locale) of a
// built-in method back to its key.
const getLabelIndex = () => {
  if (labelIndex) return labelIndex;

  labelIndex = new Map();
  const add = (text, key) => {
    const normalized = normalize(text);
    if (normalized && !labelIndex.has(normalized)) {
      labelIndex.set(normalized, key);
    }
  };

  Object.entries(BUILT_IN_METHODS).forEach(([key, label]) => {
    add(key, key);
    add(label, key);
    Object.values(i18n.options?.resources ?? {}).forEach((resource) =>
      add(resource?.translation?.[label], key)
    );
  });

  return labelIndex;
};

// The built-in methods' icons are named after their key (cash_on_delivery.png),
// and unlike `key` the icon does not change with the language.
const getIconKey = (icon) => {
  if (typeof icon !== "string") return undefined;

  const fileName = icon.split(/[?#]/)[0].split("/").pop() ?? "";
  const key = fileName.replace(/\.[a-z0-9]+$/i, "").toLowerCase();

  return Object.prototype.hasOwnProperty.call(BUILT_IN_METHODS, key)
    ? key
    : undefined;
};

/**
 * The value to send as `payment_method` for a method returned by the API.
 *
 * The API translates `key` along with the rest of the response (Arabic gives
 * `الدفع_نقداً_عند_التسليم` for `cash_on_delivery`) and then rejects it when
 * the order is submitted. A built-in key is therefore recovered, in order,
 * from the key itself, the icon, then the key and the name read as labels in
 * any supported language. Anything unrecognised is returned as it was
 * received.
 */
export const getPaymentMethodKey = (method) => {
  const key = method?.payment_method || method?.key;

  if (typeof key !== "string") return key;
  if (Object.prototype.hasOwnProperty.call(BUILT_IN_METHODS, key)) return key;

  const index = getLabelIndex();
  return (
    getIconKey(method?.icon) ??
    index.get(normalize(key)) ??
    index.get(normalize(method?.name)) ??
    key
  );
};

export const normalizePaymentMethods = (methods) =>
  (methods ?? []).map((method) => ({
    ...method,
    payment_method: getPaymentMethodKey(method),
  }));
